import React, { useState, useRef, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Alert,
  Paper,
  useTheme,
  Fade,
  Collapse,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Settings as SettingsIcon,
  KeyboardArrowDown,
  Check,
  Warning,
  Refresh
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useScandiumMutation, useScandiumQuery } from '../../data-layer/utils';
import { useActiveProject } from '../../store/projectState';
import UserContext from '../../store/userState';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';

export default function TransferOwnershipPage() {
  const theme = useTheme();
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [projectNameConfirmation, setProjectNameConfirmation] = useState('');
  const [memberSearchQuery, setMemberSearchQuery] = useState('');
  const activeProject = useActiveProject();
  const menuRef = useRef(null);
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: companiesData,
    isLoading: isLoadingCompaniesData,
    error: companiesError,
    refetch: refetchCompanies
  } = useScandiumQuery(`/projects/transfer/companies`, {
    enabled: !!activeProject?.id
  });

  const {
    data: companyMembers,
    isLoading: isLoadingCompaniesMembers,
    error: companyMembersError,
    refetch: refetchCompanyMembers
  } = useScandiumQuery(`/projects/transfer/company/${selectedOrg?.id}/users`, {
    enabled: !!activeProject?.id && !!selectedOrg?.id,
    queryKey: [selectedOrg?.id]
  });

  const { mutateAsync: resolveTransfer, isPending } = useScandiumMutation(`/projects/transfer`, {
    onError: (error) => {
      toast.error(error.message);
    },
    onSuccess: (data) => {
      toast.success(data.message);
      setConfirmationOpen(false);
      setProjectNameConfirmation('');
      queryClient.clear();
      navigate('/dashboard')
    }
  });

  const handleOrgSelect = (org) => {
    setSelectedOrg(org);
    setSelectedMember(null);
    setOrgMenuOpen(false);
  };

  const handleMemberSelect = (member) => {
    setSelectedMember(member);
  };

  const handleSubmit = () => {
    if (selectedOrg && selectedMember) {
      setConfirmationOpen(true);
    }
  };

  const confirmTransfer = async () => {
    if (projectNameConfirmation === activeProject?.name) {
      await toast.promise(
        resolveTransfer({
          destination_organization_id: selectedOrg?.id || '',
          project_id: activeProject?.id,
          recipient_id: selectedMember?.id || '',
          source_organization_id: userData?.user?.active_company_id?.toString() || ''
        }),
        {
          pending: 'Transferring ownership...'
        }
      );
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOrgMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredMembers = companyMembers?.data?.filter(
    (member) =>
      member.id !== userData?.user?.id &&
      member.name.toLowerCase().includes(memberSearchQuery.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      <Fade in={true} timeout={500}>
        <Card
          sx={{
            maxWidth: 800,
            mx: 'auto',
            bgcolor: 'background.paper',
            boxShadow: theme.shadows[1]
          }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
              <SettingsIcon
                sx={{
                  color: 'primary.main',
                  fontSize: 32
                }}
              />
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.primary'
                }}>
                Transfer Project Ownership
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                mb: 4,
                lineHeight: 1.6
              }}>
              Transferring ownership will give the selected member full control over this project.
            </Typography>

            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'medium',
                  color: 'text.primary',
                  mb: 2
                }}>
                Select Organization
              </Typography>

              <Box ref={menuRef} sx={{ position: 'relative' }}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => !isLoadingCompaniesData && setOrgMenuOpen(!orgMenuOpen)}
                  disabled={isLoadingCompaniesData}
                  sx={{
                    justifyContent: 'space-between',
                    py: 2,
                    px: 3,
                    borderColor: 'divider',
                    bgcolor: true ? 'background.paper' : 'action.disabledBackground',
                    color: true ? 'text.primary' : 'text.disabled',
                    textTransform: 'none',
                    fontSize: '1rem'
                  }}>
                  <Typography noWrap sx={{ flex: 1, textAlign: 'left' }}>
                    {selectedOrg ? selectedOrg.name : 'Select an organization'}
                  </Typography>
                  {isLoadingCompaniesData ? (
                    <CircularProgress size={20} sx={{ color: 'text.secondary' }} />
                  ) : (
                    <KeyboardArrowDown
                      sx={{
                        transition: 'transform 0.2s',
                        transform: orgMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)'
                      }}
                    />
                  )}
                </Button>

                {companiesError && (
                  <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    action={
                      <IconButton
                        size="small"
                        onClick={() => refetchCompanies()}
                        sx={{ color: 'inherit' }}>
                        <Refresh fontSize="small" />
                      </IconButton>
                    }>
                    {companiesError
                      ? companiesError.message || 'Failed to load organizations'
                      : 'Failed to load organizations'}
                  </Alert>
                )}

                <Menu
                  open={orgMenuOpen}
                  onClose={() => setOrgMenuOpen(false)}
                  anchorEl={menuRef.current}
                  sx={{
                    '& .MuiPaper-root': {
                      width: menuRef.current?.clientWidth,
                      maxHeight: 240,
                      mt: 1
                    }
                  }}>
                  {isLoadingCompaniesData ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : companiesData?.data?.length ? (
                    companiesData.data.map((org) => (
                      <MenuItem
                        key={org.id}
                        onClick={() => handleOrgSelect(org)}
                        selected={selectedOrg?.id === org.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                        {org.name}
                        {selectedOrg?.id === org.id && (
                          <Check sx={{ color: 'primary.main', ml: 2 }} />
                        )}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No organizations found</MenuItem>
                  )}
                </Menu>
              </Box>
            </Box>

            <Collapse in={!!selectedOrg}>
              <Box sx={{ mb: 4 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'medium',
                    color: 'text.primary',
                    mb: 2
                  }}>
                  Select Member from {selectedOrg?.name}
                </Typography>

                <TextField
                  fullWidth
                  placeholder="Search members..."
                  value={memberSearchQuery}
                  onChange={(e) => setMemberSearchQuery(e.target.value)}
                  sx={{ mb: 3 }}
                />

                {isLoadingCompaniesMembers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : companyMembersError ? (
                  <Alert
                    severity="error"
                    action={
                      <IconButton
                        size="small"
                        onClick={() => refetchCompanyMembers()}
                        sx={{ color: 'inherit' }}>
                        <Refresh fontSize="small" />
                      </IconButton>
                    }>
                    {companyMembersError
                      ? companyMembersError?.message || 'Failed to load members'
                      : 'Failed to load members'}
                  </Alert>
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      maxHeight: 400,
                      overflow: 'auto',
                      bgcolor: 'background.default'
                    }}>
                    <List>
                      {!filteredMembers?.length ? (
                        <ListItem>
                          <ListItemText
                            primary={
                              <Typography textAlign="center" color="text.secondary">
                                {memberSearchQuery
                                  ? `No members found matching "${memberSearchQuery}"`
                                  : 'No members found in this organization'}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ) : (
                        filteredMembers.map((member) => (
                          <ListItem
                            key={member.id}
                            onClick={() => handleMemberSelect(member)}
                            sx={{
                              cursor: 'pointer',
                              border: selectedMember?.id === member.id ? 2 : 1,
                              borderColor:
                                selectedMember?.id === member.id ? 'primary.main' : 'divider',
                              bgcolor:
                                selectedMember?.id === member.id
                                  ? 'primary.light'
                                  : 'background.paper',
                              mb: 1,
                              borderRadius: 1,
                              '&:hover': {
                                bgcolor:
                                  selectedMember?.id === member.id
                                    ? 'primary.light'
                                    : 'action.hover'
                              }
                            }}>
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: 'primary.main' }}>
                                {member.name.charAt(0)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="subtitle1" fontWeight="medium">
                                  {member.name}
                                </Typography>
                              }
                              secondary={member.email}
                            />
                            {selectedMember?.id === member.id && (
                              <Check sx={{ color: 'primary.main' }} />
                            )}
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Paper>
                )}
              </Box>
            </Collapse>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!selectedOrg || !selectedMember || isLoadingCompaniesMembers || isPending}
                sx={{
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontSize: '1rem',
                  minWidth: 160
                }}>
                {isPending ? (
                  <CircularProgress size={20} sx={{ color: 'white' }} />
                ) : (
                  'Transfer Ownership'
                )}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Fade>

      <Dialog
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Warning sx={{ color: 'warning.main' }} />
          Confirm Ownership Transfer
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to transfer ownership of <strong>{activeProject?.name}</strong> to{' '}
            <strong>{selectedMember?.name}</strong> from <strong>{selectedOrg?.name}</strong>.
          </Typography>

          <Typography variant="body2" sx={{ mb: 3, fontWeight: 'medium' }}>
            This action cannot be undone. Type the project name below to confirm:
          </Typography>

          <TextField
            fullWidth
            value={projectNameConfirmation}
            onChange={(e) => setProjectNameConfirmation(e.target.value)}
            placeholder={activeProject?.name}
            variant="outlined"
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setConfirmationOpen(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmTransfer}
            disabled={projectNameConfirmation !== activeProject?.name}
            sx={{ textTransform: 'none' }}>
            Confirm Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
